import {
	Context,
	ContextContent,
	ContextContentBody,
	ContextContentHeader,
	ContextTrigger,
} from "@/components/ai-elements/context";
import { Shimmer } from "@/components/ai-elements/shimmer";
import { Button } from "@/components/ui/button";
import { AgentAvatar } from "./AgentAvatar.js";
import type { ChatPersona } from "./mairko-chat.js";
import { ArrowLeftIcon, InfoIcon } from "lucide-react";

type Progress = {
	current: number;
	total: number;
	label: string;
};

type Props = {
	agent: ChatPersona;
	progress?: Progress | null;
	readyLabel?: string;
	onOpenInfo?: () => void;
	onBack?: () => void;
};

/** Slim chat chrome — sidebar toggle lives in ShellToolbar. */
export function AgentChrome({
	agent,
	progress,
	readyLabel,
	onOpenInfo,
	onBack,
}: Props) {
	const inSetup = Boolean(progress && progress.total > 0);

	return (
		<header className="agent-chrome">
			<div className="agent-chrome-left">
				{onBack && (
					<Button
						type="button"
						variant="ghost"
						size="icon-sm"
						className="shrink-0 text-muted-foreground"
						onClick={onBack}
						aria-label="Back to Agents desk"
					>
						<ArrowLeftIcon className="size-4" />
					</Button>
				)}
				<AgentAvatar agent={agent} size="sm" />
				<div className="agent-chrome-copy">
					<p className="agent-chrome-name">{agent.name}</p>
					{inSetup && progress ? (
						<p className="agent-chrome-sub">{progress.label}</p>
					) : readyLabel ? (
						<Shimmer
							as="p"
							className="agent-chrome-sub"
							duration={2.6}
						>
							{`${agent.department} · ${readyLabel}`}
						</Shimmer>
					) : (
						<p className="agent-chrome-sub">{agent.department}</p>
					)}
				</div>
			</div>

			<div className="agent-chrome-actions">
				{onOpenInfo && (
					<Button
						type="button"
						variant="ghost"
						size="sm"
						className="h-8 gap-1.5 px-2 text-muted-foreground"
						onClick={onOpenInfo}
					>
						<InfoIcon className="size-3.5" />
						Profile
					</Button>
				)}
				{inSetup && progress ? (
					<Context
						usedTokens={progress.current}
						maxTokens={progress.total}
					>
						<ContextTrigger className="agent-chrome-context h-8 gap-1.5 px-2" />
						<ContextContent align="end" className="w-64">
							<ContextContentHeader>
								<div className="flex items-center justify-between gap-3 text-xs">
									<p className="font-medium">Setup</p>
									<p className="font-mono text-muted-foreground">
										{progress.current} / {progress.total}
									</p>
								</div>
							</ContextContentHeader>
							<ContextContentBody className="pt-0">
								<p className="m-0 text-xs text-muted-foreground">
									{progress.label}
								</p>
							</ContextContentBody>
						</ContextContent>
					</Context>
				) : null}
			</div>
		</header>
	);
}
